import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Basket, ArrowsClockwise, MagnifyingGlass, Globe, UsersThree, CircleNotch, X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Card, Loading } from "@/components/riq";
import { AdminHpiReports } from "@/components/AdminHpiReports";

const r2 = (v) => (typeof v === "number" ? `R${v.toFixed(2)}` : "—");
const unitLabel = (u) => (u === "g" ? "/100g" : u === "ml" ? "/100ml" : "/unit");

function CategoryTable({ cats, active, onPick }) {
  return (
    <Card className="!p-0 overflow-hidden" data-testid="hpi-categories">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-xs uppercase tracking-wide text-muted-foreground">
          <tr><th className="text-left px-4 py-2">Category</th><th className="px-3">Items</th><th className="px-3">Priced</th>
            <th className="px-3">CPI-style wt</th><th className="px-3">Sliply spend wt</th></tr>
        </thead>
        <tbody>
          {cats.map((c) => (
            <tr key={c.key} data-testid={`hpi-cat-${c.key}`} onClick={() => onPick(active === c.key ? "" : c.key)}
              className={`border-t border-riq-border cursor-pointer transition-colors ${active === c.key ? "bg-riq-primary/10" : "hover:bg-neutral-50"}`}>
              <td className="px-4 py-2 font-medium">{c.name}</td>
              <td className="text-center tabular-nums">{c.items}</td>
              <td className="text-center tabular-nums">
                <span className={c.priced ? "text-emerald-700 font-medium" : "text-muted-foreground"}>{c.priced}</span>
                <span className="text-xs text-muted-foreground"> ({Math.round((c.priced / c.items) * 100)}%)</span>
              </td>
              <td className="text-center tabular-nums">{c.category_weight}%</td>
              <td className="text-center tabular-nums">{c.spend_weight}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function ItemDialog({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/hpi/items/${id}`).then((r) => setD(r.data)).catch(() => setD(false)); }, [id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="hpi-item-dialog" className="w-full max-w-2xl max-h-[85vh] overflow-auto rounded-3xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <button data-testid="hpi-item-dialog-close" onClick={onClose} className="float-right text-muted-foreground hover:text-neutral-900"><X size={20} /></button>
        {!d ? (d === false ? <p>Could not load item.</p> : <Loading />) : (
          <>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{d.item.category_name}</div>
            <h3 className="font-head text-xl font-bold">{d.item.name}</h3>
            <div className="mt-1 text-xs text-muted-foreground">
              Search: "{d.item.query}" · CPI-style weight {d.item.category_weight}% · Sliply spend weight {d.item.spend_weight ?? 0}%
              {d.item.spend_total_12m ? ` (R${d.item.spend_total_12m.toLocaleString()} in 12m)` : ""}
            </div>
            {!d.months.length && <p className="mt-6 text-sm text-muted-foreground">No prices collected yet.</p>}
            {d.months.map((m) => (
              <div key={m.month} className="mt-5">
                <div className="flex items-baseline justify-between">
                  <div className="font-semibold">{m.month}</div>
                  <div className="text-xs text-muted-foreground">median {r2(m.stats.median)}{unitLabel(d.item.unit)} · {m.stats.stores} stores</div>
                </div>
                <div className="mt-2 divide-y divide-riq-border rounded-2xl border border-riq-border">
                  {m.observations.map((o, i) => (
                    <div key={i} data-testid={`hpi-obs-${m.month}-${i}`} className="flex items-center gap-3 px-3 py-2 text-sm">
                      {o.source === "online" ? <Globe size={15} className="text-sky-600 shrink-0" /> : <UsersThree size={15} className="text-amber-600 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">{o.store}{o.province ? <span className="text-xs text-muted-foreground"> · {o.province}</span> : null}</div>
                        <div className="truncate text-xs text-muted-foreground" title={o.title}>
                          {o.url ? <a href={o.url} target="_blank" rel="noreferrer" className="hover:text-riq-primary">{o.title}</a> : o.title}
                          {o.count > 1 ? ` · ${o.count} receipts` : ""}
                        </div>
                      </div>
                      <div className="text-right tabular-nums">
                        <div className="font-medium">{r2(o.price)}</div>
                        <div className="text-xs text-muted-foreground">{r2(o.unit_price)}{unitLabel(d.item.unit)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminHPI() {
  const [view, setView] = useState("reports");
  const tabs = [["reports", "Index & reports"], ["basket", "Basket prices"]];
  return (
    <div className="space-y-6">
      <div className="inline-flex rounded-full border border-riq-border bg-white p-1" data-testid="hpi-subtabs">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`hpi-subtab-${k}`} onClick={() => setView(k)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${view === k ? "bg-riq-primary text-white" : "text-neutral-600 hover:text-riq-primary"}`}>{l}</button>
        ))}
      </div>
      {view === "reports" ? <AdminHpiReports /> : <BasketPrices />}
    </div>
  );
}

function BasketPrices() {
  const [sum, setSum] = useState(null);
  const [month, setMonth] = useState("");
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [priced, setPriced] = useState("");
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSum = useCallback(() => api.get(`/admin/hpi/summary${month ? `?month=${month}` : ""}`).then((r) => setSum(r.data)).catch(() => setSum(false)), [month]);
  useEffect(() => { loadSum(); }, [loadSum]);
  useEffect(() => {
    const p = new URLSearchParams();
    if (month) p.set("month", month);
    if (cat) p.set("category", cat);
    if (q.trim()) p.set("q", q.trim());
    if (priced) p.set("priced", priced);
    const t = setTimeout(() => api.get(`/admin/hpi/items?${p}`).then((r) => setItems(r.data.items)).catch(() => setItems([])), 250);
    return () => clearTimeout(t);
  }, [month, cat, q, priced, sum?.last_collect?.finished_at]);

  const collect = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/hpi/collect${month ? `?month=${month}` : ""}`);
      toast.message("Collecting basket prices…");
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data } = await api.get(`/admin/hpi/summary${month ? `?month=${month}` : ""}`);
        if (!data.running) { setSum(data); toast.success(`Collected: ${data.observations} prices for ${data.priced} items`); break; }
      }
    } catch { toast.error("Collection failed"); } finally { setBusy(false); }
  };

  if (sum === false) return <Card>Could not load the HPI basket.</Card>;
  if (!sum) return <Loading />;
  const lc = sum.last_collect;

  return (
    <div className="space-y-6" data-testid="admin-hpi">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-riq-primary"><Basket size={22} weight="duotone" /><h2 className="font-head text-xl font-bold">Household Price Index basket</h2></div>
          <p className="text-xs text-muted-foreground mt-1">
            {sum.items} items · prices from scraped retailers and users' receipts, stored with unit prices (R/100g, R/100ml, R/unit) and province.
            The monthly index and reports are built from these prices under "Index & reports".
          </p>
        </div>
        <select data-testid="hpi-month" value={month || sum.month} onChange={(e) => setMonth(e.target.value)}
          className="rounded-full border border-riq-border px-3 py-2 text-sm bg-white">
          {sum.months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <button data-testid="hpi-collect-btn" onClick={collect} disabled={busy || sum.running}
          className="flex items-center gap-2 rounded-full bg-riq-primary px-4 py-2 text-sm font-medium text-white hover:-translate-y-0.5 transition-transform disabled:opacity-60">
          {busy || sum.running ? <CircleNotch size={16} className="animate-spin" /> : <ArrowsClockwise size={16} weight="bold" />} Collect prices now
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[["Items priced", `${sum.priced} / ${sum.items}`, "hpi-stat-priced"], ["Price observations", sum.observations, "hpi-stat-obs"],
          ["Online · Receipts", `${sum.by_source.online} · ${sum.by_source.receipt}`, "hpi-stat-sources"],
          ["Last collected", lc ? `${lc.finished_at.slice(0, 16).replace("T", " ")}` : "Never", "hpi-stat-last"]].map(([l, v, t]) => (
          <Card key={t} className="!p-4" data-testid={t}>
            <div className="text-xs text-muted-foreground">{l}</div>
            <div className="mt-1 font-head text-xl font-bold tabular-nums">{v}</div>
          </Card>
        ))}
      </div>

      <CategoryTable cats={sum.categories} active={cat} onPick={setCat} />

      <Card className="!p-0 overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 p-3 border-b border-riq-border">
          <div className="flex items-center gap-2 flex-1 min-w-[200px] rounded-full border border-riq-border px-3 py-1.5">
            <MagnifyingGlass size={15} className="text-muted-foreground" />
            <input data-testid="hpi-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search basket items…" className="flex-1 bg-transparent text-sm outline-none" />
          </div>
          {[["", "All"], ["true", "Priced"], ["false", "Not priced"]].map(([v, l]) => (
            <button key={l} data-testid={`hpi-filter-${l.toLowerCase().replace(" ", "-")}`} onClick={() => setPriced(v)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${priced === v ? "bg-neutral-900 text-white" : "border border-riq-border hover:border-riq-primary"}`}>{l}</button>
          ))}
          {cat && <button data-testid="hpi-clear-cat" onClick={() => setCat("")} className="rounded-full bg-riq-primary/10 px-3 py-1.5 text-xs font-medium text-riq-primary">{sum.categories.find((c) => c.key === cat)?.name} ×</button>}
        </div>
        {!items ? <Loading /> : (
          <div className="max-h-[560px] overflow-auto">
            <table className="w-full text-sm" data-testid="hpi-items-table">
              <thead className="sticky top-0 bg-neutral-50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr><th className="text-left px-4 py-2">Item</th><th className="px-2">Wt (CPI / spend)</th><th className="px-2">Stores</th>
                  <th className="px-2 text-right">Min (unit)</th><th className="px-2 text-right">Median</th><th className="px-4 text-right">Max</th></tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} data-testid={`hpi-item-${it.id}`} onClick={() => setOpen(it.id)} className="border-t border-riq-border cursor-pointer hover:bg-neutral-50">
                    <td className="px-4 py-2">
                      <div className="font-medium">{it.name}</div>
                      {!cat && <div className="text-[11px] text-muted-foreground">{it.category_name}</div>}
                    </td>
                    <td className="text-center text-xs tabular-nums">{it.category_weight.toFixed(3)} / {(it.spend_weight || 0).toFixed(2)}</td>
                    <td className="text-center tabular-nums">{it.stats.n ? it.stats.stores : <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-2 text-right tabular-nums">{it.stats.n ? <>{r2(it.stats.min)}<span className="text-[10px] text-muted-foreground">{unitLabel(it.unit)}</span></> : ""}</td>
                    <td className="px-2 text-right tabular-nums font-medium">{it.stats.n ? <>{r2(it.stats.median)}<span className="text-[10px] text-muted-foreground">{unitLabel(it.unit)}</span></> : ""}</td>
                    <td className="px-4 text-right tabular-nums">{it.stats.n ? <>{r2(it.stats.max)}<span className="text-[10px] text-muted-foreground">{unitLabel(it.unit)}</span></> : ""}</td>
                  </tr>
                ))}
                {!items.length && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No items match.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {open && <ItemDialog id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
